import React from "react";

import { useMutation, useQuery } from "@apollo/client";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import { Box, Theme, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import DeleteModal from "@/components/DeleteModal";
import IconButton from "@/components/IconButtons/IconButton";
import WaterIntakeGoalCircularIcon from "@/components/Icons/WaterIntakeGoalCircularIcon";
import { WeighingScaleCircularIcon } from "@/components/Icons/WeighingScaleCircularIcon";
import { Features } from "@/config/features";
import { graphql } from "@/gql";
import useDialogState from "@/hooks/useDialogState";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import AddWaterGoalModal from "@/pages/Clients/ClientDetails/ClientDiary/WaterIntake/components/AddWaterGoalModal";
import EditWaterGoalModal from "@/pages/Clients/ClientDetails/ClientDiary/WaterIntake/components/EditWaterGoalModal";
import { waterIntakePageQuery } from "@/pages/Clients/ClientDetails/ClientDiary/WaterIntake/WaterIntakePage";
import EditWeightGoalModal from "@/pages/Clients/ClientDetails/ClientDiary/Weights/components/EditWeightGoalModal";
import { displayError } from "@/utils/errors";

const useStyles = makeStyles()((theme: Theme) => ({
  card: {
    backgroundColor: "white",
    padding: "20px",
    marginTop: "20px",
  },
  subHeader1: {
    fontFamily: "Mulish",
    fontWeight: 700,
    fontSize: "24px",
  },
  body1: {
    fontFamily: "Mulish",
    fontWeight: 400,
    fontSize: "16px",
    color: "#363E45",
  },
  body1HightLighted: {
    fontFamily: "Mulish",
    fontWeight: 700,
    fontSize: "16px",
    color: "#0B1B26",
  },
  iconContainer: {
    background: "#EDF0F5",
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    marginRight: theme.spacing(2),
  },
}));

export const clientWeightLogsPageQuery = graphql(`
  query ClientWeightLogsPage($date: ISO8601Date, $clientId: ID!) {
    dailyWeights(date: $date, clientId: $clientId) {
      id
      date
      lastWeightKg
      logs {
        ...PastLog
      }
    }
    client(id: $clientId) {
      id
      journalGoal {
        id
        targetWeightKg
      }
      goals {
        nodes {
          id
          title
        }
      }
    }
  }
`);

const updateWeightGoalMutation = graphql(`
  mutation UpdateWeightGoal(
    $updateGoalInput: UpdateGoalInput!
    $updateJournalGoalInput: UpdateJournalGoalInput!
  ) {
    updateGoal(input: $updateGoalInput) {
      goal {
        id
        title
      }
      errors {
        fullMessage
      }
    }
    updateJournalGoal(input: $updateJournalGoalInput) {
      journalGoal {
        id
        targetWeightKg
      }
      errors {
        fullMessage
      }
    }
  }
`);

export const Goals: React.FC = () => {
  const editCurrentWeightDialog = useDialogState();
  const deleteCurrentWeightDialog = useDialogState();
  const editCurrentWaterGoalDialog = useDialogState();
  const addCurrentWaterGoalDialog = useDialogState();
  const snackbar = useGlobalSnackbar();
  const { clientId, date } = useParams<{ clientId: string; date: string }>();
  const { data, refetch } = useQuery(clientWeightLogsPageQuery, {
    variables: { clientId: clientId ?? "", date },
  });
  const { data: clientData } = useQuery(waterIntakePageQuery, {
    variables: { date: date, clientId: clientId ?? "" },
  });
  const [deleteWeightGoal] = useMutation(updateWeightGoalMutation);

  const waterIntakeGoal = clientData?.client?.journalGoal?.targetWaterIntakeMl;
  const targetWeight = data?.client?.journalGoal?.targetWeightKg;
  const goalDescription = data?.client?.goals.nodes?.[0]?.title;
  const goalId = data?.client?.goals.nodes?.[0]?.id;

  const { classes } = useStyles();

  const handleDeleteWeightGoal = () => {
    deleteWeightGoal({
      variables: {
        updateGoalInput: {
          id: goalId ?? "",
          clientId: clientId,
          patch: { title: "" },
        },
        updateJournalGoalInput: {
          clientId: clientId,
          attributes: { targetWeightKg: null },
        },
      },
    })
      .then(() => {
        snackbar.open("Goal weight deleted successfully");
        refetch();
      })
      .catch((e) => {
        snackbar.open(displayError(e, "Unable to delete goal weight"));
      });
  };

  return (
    <Box className={classes.card}>
      <Typography className={classes.subHeader1}>Goals</Typography>
      <Box height="20px" />
      <Box display="flex">
        <Box display="flex" flex={1}>
          <Box className={classes.iconContainer}>
            <WaterIntakeGoalCircularIcon />
          </Box>
          <Box width="15px" />
          <Box display="flex" alignItems="flex-start">
            <Box>
              <Typography className={classes.body1}>
                Water Intake Goal
              </Typography>
              <Typography className={classes.body1HightLighted}>
                {waterIntakeGoal ? `${waterIntakeGoal} ml` : "N/A"}
              </Typography>
            </Box>
            <Box width="30px" />
            {Features.waterIntakeGoalEnabled ? (
              <IconButton
                onClick={editCurrentWaterGoalDialog.show}
                title="Edit"
                startIcon={<EditRoundedIcon fontSize="small" />}
              />
            ) : (
              <IconButton
                onClick={addCurrentWaterGoalDialog.show}
                title="Add Goal"
                startIcon={<AddRoundedIcon fontSize="small" />}
              />
            )}
          </Box>
        </Box>
        <Box flex={1} display="flex" alignItems="flex-start">
          <Box className={classes.iconContainer}>
            <WeighingScaleCircularIcon />
          </Box>
          <Box width="15px" />
          <Box display="flex" alignItems="flex-start">
            <Box>
              <Typography className={classes.body1}>Goal Weight</Typography>
              <Typography className={classes.body1HightLighted}>
                {targetWeight ? `${targetWeight} kg` : "N/A"}
              </Typography>
            </Box>
            <Box width="30px" />
            {targetWeight ? (
              <>
                <IconButton
                  onClick={editCurrentWeightDialog.show}
                  title="Edit"
                  startIcon={<EditRoundedIcon fontSize="small" />}
                />
                <Box width={10} />
                <IconButton
                  onClick={deleteCurrentWeightDialog.show}
                  title="Remove Goal"
                  startIcon={<DeleteRoundedIcon fontSize="small" />}
                />
              </>
            ) : (
              <IconButton
                onClick={editCurrentWeightDialog.show}
                title="Add Goal"
                startIcon={<AddRoundedIcon fontSize="small" />}
              />
            )}
          </Box>
        </Box>

        <DeleteModal
          title="Delete goal weight"
          message="Do you really want to delete goal weight?"
          visible={deleteCurrentWeightDialog.shown}
          onClose={deleteCurrentWeightDialog.hide}
          onDelete={handleDeleteWeightGoal}
        />

        <EditWeightGoalModal
          visibility={editCurrentWeightDialog.shown}
          onClose={() => editCurrentWeightDialog.hide()}
          weightGoal={targetWeight}
          title={goalDescription ?? "Edit Goal Weight"}
          goalPresent={true}
          modalTitle={targetWeight ? "Edit" : "Add"}
        />

        <AddWaterGoalModal
          onClose={addCurrentWaterGoalDialog.hide}
          visibility={addCurrentWaterGoalDialog.shown}
        />

        <EditWaterGoalModal
          onClose={editCurrentWaterGoalDialog.hide}
          visibility={editCurrentWaterGoalDialog.shown}
          currentWaterGoal={waterIntakeGoal ? waterIntakeGoal.toString() : "0"}
        />
      </Box>
    </Box>
  );
};
